import React, { FC, useRef, useState, useEffect } from 'react';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import {
  BlogSortBy,
  ProposalsSortBy,
  SortBy,
  SortDirection
} from 'src/api/types';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import DropdownElement from '../Dropdown/DropdownElement';
import SortList from './SortList';
import Item from './Item';
import { sortOptionList } from './constants';
import NextImage from '../NextImage/NextImage';

interface Props {
  bgDarkStyle?: boolean;
  disabled?: boolean;
  className?: string;
  gtagPrefix?: GTAGPrefix;
  onToggle?: (isOpen: boolean) => void;
  proposalsSortList: (SortBy | ProposalsSortBy | BlogSortBy)[];
  proposalSortProps?: {
    sortBy: SortBy | ProposalsSortBy | BlogSortBy;
    sortDirection: SortDirection;
  };
  onProposalsSortChange: (sortProps: {
    sortBy: SortBy | ProposalsSortBy | BlogSortBy;
    sortDirection: SortDirection;
  }) => void;
  style?: { [key: string]: string };
}

const SortingDropdown: FC<Props> = ({
  className = '',
  gtagPrefix,
  proposalsSortList,
  proposalSortProps = {},
  onProposalsSortChange,
  onToggle = () => null,
  disabled = false,
  bgDarkStyle,
  style
}) => {
  const [sortValue, setSortValue] = useState<string>('SORT');
  const sortValueKey = Object.keys(sortOptionList)?.find(
    (option) => option.toLowerCase() === sortValue.toLowerCase()
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const intl = useIntl();
  const iconArrowDown = 'shared/arrow-down.svg';

  const { trackGtagEvent } = useGTAG();

  const [isSortSelectModalVisible, setIsSortSelectModalVisible] =
    useState<boolean>(false);

  useEffect(() => {
    if (proposalSortProps?.sortBy && proposalSortProps?.sortDirection) {
      setSortValue(
        `${proposalSortProps?.sortBy}_${proposalSortProps?.sortDirection}`
      );
    } else {
      setSortValue('SORT');
    }
  }, [proposalSortProps?.sortBy, proposalSortProps?.sortDirection]);

  useEffect(() => {
    if (disabled) {
      setIsSortSelectModalVisible(false);
    }
  }, [disabled]);

  const handleSortingChange = ({ sortBy, sortDirection }) => {
    setSortValue(`${sortBy}_${sortDirection}`);
    setIsSortSelectModalVisible(false);
    onToggle(false);
    onProposalsSortChange({
      sortBy,
      sortDirection
    });
  };

  const onClickHandler = () => {
    if (!disabled) {
      setIsSortSelectModalVisible(!isSortSelectModalVisible);
    }
    onToggle(!isSortSelectModalVisible);
    trackGtagEvent(`${gtagPrefix}_sort`);
  };

  return (
    <>
      <button
        className={cn(
          'fs-caption-reg bg-transparent outline-none flex flex-row flex-nowrap whitespace-nowrap items-center h-full',
          className
        )}
        onClick={onClickHandler}
        ref={buttonRef}
      >
        <Item
          {...sortOptionList[sortValueKey]}
          title={intl.formatMessage(sortOptionList[sortValueKey].title)}
          bgDarkStyle={bgDarkStyle}
        />
        <NextImage
          alt="arrow down"
          src={iconArrowDown}
          height={24}
          width={24}
          className={cn({
            'rotate-180': iconArrowDown && isSortSelectModalVisible
          })}
          priority
        />
      </button>
      <DropdownElement
        customStyles={style}
        parentRef={buttonRef}
        isOpen={!disabled && isSortSelectModalVisible}
        placement="bottom-end"
        className="bg-white shadow-lg rounded-lg z-40 sm:max-w-[250px] flex flex-col items-center ml-0 mt-0 top-[5px]"
      >
        <SortList
          proposalsSortList={proposalsSortList}
          selectedProposalsSort={proposalSortProps}
          onProposalsSortChange={handleSortingChange}
          gtagPrefix={gtagPrefix}
        />
      </DropdownElement>
    </>
  );
};

export default SortingDropdown;
